// ---------------------------------------------------------------------------
// Homefront v3 — crater damage (fixed footprint ladder, HEX)
// ---------------------------------------------------------------------------
//
// Winning a battle by margin M destroys a FIXED FOOTPRINT of subplots centred
// on the aim cell — a single blast the player can picture. The Grid is a hex
// lattice (hexGrid.js), so the footprint grows in hex rings, +6 cells per
// margin, NEAREST cells first (centre-to-centre), and every step fully
// contains the one below it so growth reads as one blast expanding:
//
//   margin 1 —  7 cells: the aim + ring 1
//   margin 2 — 13 cells: + ring 2's six edge midpoints (closer than its corners)
//   margin 3 — 19 cells: + ring 2's six corners — the full radius-2 hexagon
//   margin 4 — 25 cells: + one of ring 3's two edge cells on each side (the
//              first after each corner in walk order — rotationally symmetric)
//   margin 5 — 31 cells: + the other six — all twelve ring-3 edge cells
//   margin 6 — 37 cells: + ring 3's six corners — the full radius-3 hexagon
//   margin 7+ — 43 cells (cap): + ring 4's six edge midpoints (closer than
//              the rest of ring 4)
//
// Rocket interception cancels whole shots BEFORE the footprint is sized (see
// subplotRockets.resolveCrater), so the ladder is keyed on the margin that
// survives.
//
// The footprint is NOT constrained by corridor width — it expands freely into
// neighbouring corridors. Corridors govern which drones fight and where they
// may aim; they do not contain the damage. Cells falling off the grid's edge
// are simply lost (truncated, not reflected).
//
// Impact is the deterministic footprint; fire is presentation beyond it and
// destroys nothing. Destroyed subplots are marked, drop out of networks, and
// are revealed to the attacker. Nothing beyond the footprint is ever
// destroyed: a Brood UNDER CONSTRUCTION dies whole when any of its cells is
// hit (subplotBases.isBroodDead), but only as a Brood — its surviving cells
// stay intact ground and revert to open land at the corpse sweep. Distance intel — a TAGGED stamp: the attacker's letter for the
// nearest surviving enemy structure plus the HEX distance to it — is
// written at the aim cell (see stampDistanceIntel); the bare number also
// surfaces via the `distance` result field.

import { rules } from './config.js'
import { cloneHomefront, isStructureDestroyed } from './subplotGrid.js'
import { isBroodDead } from './subplotBases.js'
import { ringOffsets, isRingCorner, cellsAtOffsets, hexDist2, hexDistance } from './hexGrid.js'

// Deterministic seed for a crater, keyed on round + aim subplot (both
// perspective-independent and synced across clients). The footprint itself is
// fully deterministic; the seed still drives the retaliation aim pick and
// keeps replay/report ordering stable across clients.
export function craterSeed(round, aimS) {
    return (((round + 1) * 73856093) ^ ((aimS + 1) * 19349663)) >>> 0
}

// The blast cap — the margin-7+ footprint's cell count — and the margin at
// which the ladder stops growing.
export const MAX_CRATER_CELLS = 43
export const MAX_CRATER_MARGIN = 7

// Offset ladder (axial offsets from the aim), built once. Every shape fully
// contains the shape below it. See the header for what each step adds.
function buildShapes() {
    const off = (o) => [o.dq, o.dr]
    const r2 = ringOffsets(2), r3 = ringOffsets(3), r4 = ringOffsets(4)
    const m1 = [[0, 0], ...ringOffsets(1).map(off)]
    const m2 = [...m1, ...r2.filter(o => !isRingCorner(o.i, 2)).map(off)]
    const m3 = [...m1, ...r2.map(off)]
    const m4 = [...m3, ...r3.filter(o => o.i % 3 === 1).map(off)]
    const m5 = [...m3, ...r3.filter(o => !isRingCorner(o.i, 3)).map(off)]
    const m6 = [...m5, ...r3.filter(o => isRingCorner(o.i, 3)).map(off)]
    const m7 = [...m6, ...r4.filter(o => o.i % 4 === 2).map(off)]
    return { 1: m1, 2: m2, 3: m3, 4: m4, 5: m5, 6: m6, 7: m7 }
}
const SHAPES = buildShapes()

// The footprint's axial offsets [dq, dr] from the aim cell for a winning margin.
export function craterFootprintOffsets(margin) {
    if (margin <= 0) return []
    return SHAPES[Math.min(MAX_CRATER_MARGIN, margin)]
}

// The in-bounds subplots a crater at `aimS` with `margin` destroys, sorted
// centre-out (ties by index) so hits report — and any animation driven off it —
// reads as the blast expanding. Off-grid cells are truncated and lost.
export function craterFootprint(aimS, margin) {
    return cellsAtOffsets(aimS, craterFootprintOffsets(margin))
        .sort((a, b) => hexDist2(a, aimS) - hexDist2(b, aimS) || a - b)
}

// Reports the nearest live enemy structure to `aimS`: its HEX distance
// (in steps, to the nearest live cell of its footprint, within
// BEARING_RANGE) AND which structure it is. Structures only — resource mass and
// height are never sensed, and Bases don't count (they are visible to the
// opponent anyway; a bearing pointing at one would be junk intel). Returns
// { distance: null, structureId: null } when no structure is in range (silence
// is a valid signal). Equidistant structures tie-break to the LOWEST structure
// id — ids are iterated sorted, never in map-insertion order, so both clients
// derive the same stamp from the same submitted state regardless of how the
// structures object round-tripped. Call AFTER the crater's destruction has
// resolved so a crater that kills a structure reports the NEXT nearest
// survivor.
export function nearestStructureIntel(homefront, aimS) {
    let best = Infinity
    let bestId = null
    for (const id of Object.keys(homefront.structures || {}).sort()) {
        const st = homefront.structures[id]
        for (const s of st.footprint) {
            const cell = homefront.subplots[s]
            if (!cell || cell.destroyed || cell.hp <= 0) continue
            const d = hexDistance(aimS, s)
            if (d < best) { best = d; bestId = id }
        }
    }
    if (best === Infinity) return { distance: null, structureId: null }
    const range = rules().bearingRange
    if (range != null && best > range) return { distance: null, structureId: null }
    return { distance: best, structureId: bestId }
}

// The distance half alone — kept for callers/tests that only need the number.
export function nearestStructureDistance(homefront, aimS) {
    return nearestStructureIntel(homefront, aimS).distance
}

// The attacker-facing tag for the i-th structure contacted (0 → 'A'). Excel
// style past 'Z' (26 → 'AA'), unreachable in practice but total.
export function intelLetter(i) {
    let out = ''
    for (let n = i; n >= 0; n = Math.floor(n / 26) - 1) {
        out = String.fromCharCode(65 + (n % 26)) + out
    }
    return out
}

// Writes a tagged distance stamp at `aimS`: the nearest-survivor distance plus
// the letter naming WHICH structure it refers to. First contact with a
// structure mints its letter into homefront.intelLetters, in contact order —
// letters are per-war, per-attacker (one ledger per board, see subplotGrid)
// and never reused or reassigned. The stamp is written once and never
// updates; dead-marking (sweepDeadStamps) is the only later change. Shared by
// crater aims and drone scan centers so every stamp on a board speaks the
// same format. Mutates the given homefront; returns the stamp.
export function stampDistanceIntel(homefront, aimS) {
    const { distance, structureId } = nearestStructureIntel(homefront, aimS)
    let letter = null
    if (structureId != null) {
        const letters = homefront.intelLetters || (homefront.intelLetters = {})
        if (letters[structureId] == null) letters[structureId] = intelLetter(Object.keys(letters).length)
        letter = letters[structureId]
    }
    const cell = homefront.subplots[aimS]
    cell.distance = distance
    cell.distanceLetter = letter
    cell.distanceDead = false
    return { distance, letter, structureId }
}

// One-shot upgrade for wars saved BEFORE tagging: letters their legacy bare-
// number stamps. Recoverable because the old expiry sweep nulled any stamp the
// layout made stale — so every surviving letterless stamp is still accurate,
// and the structure it refers to is exactly the nearest survivor at the
// stamp's own distance (same tie-break as a fresh stamp). Letters mint in
// board-scan order; both clients run this on the same persisted snapshot at
// resolution, so they derive identical ledgers, and the persisted result makes
// it permanent. A stamp whose recomputed distance no longer matches (its
// structure died between deploy and this migration) names nothing recoverable
// and stays letterless — it renders as a bare number forever. Idempotent.
// Mutates the given homefront.
export function migrateLegacyStamps(homefront) {
    homefront.subplots.forEach((cell, s) => {
        if (!cell || cell.distance == null || cell.distanceLetter != null) return
        const { distance, structureId } = nearestStructureIntel(homefront, s)
        if (structureId == null || distance !== cell.distance) return
        const letters = homefront.intelLetters || (homefront.intelLetters = {})
        if (letters[structureId] == null) letters[structureId] = intelLetter(Object.keys(letters).length)
        cell.distanceLetter = letters[structureId]
        cell.distanceDead = false
    })
}

// Dead-marks stamps whose lettered structure has been fully destroyed: the
// stamp STAYS on the board (greyed by the renderer), it is never removed and
// its letter is never reassigned — the ledger only grows. This replaces the
// old expiry sweep: a stamp names a specific structure now, so it goes dead
// exactly when THAT structure dies, and other kills leave it untouched.
// Legacy letterless stamps (pre-tagging saves) name nothing and are left
// alone. Mutates the given homefront.
export function sweepDeadStamps(homefront) {
    const letters = homefront.intelLetters || {}
    const deadLetters = new Set(
        Object.keys(letters).filter(id => isStructureDestroyed(homefront, id)).map(id => letters[id])
    )
    if (deadLetters.size === 0) return
    for (const cell of homefront.subplots) {
        if (cell?.distanceLetter != null && deadLetters.has(cell.distanceLetter)) cell.distanceDead = true
    }
}

// Applies a crater to `homefront` at aim subplot `aimS` for a winning `margin`.
// Pure: returns a fresh homefront plus a full report. The footprint is fully
// deterministic; `seed` is accepted for call-site stability but unused.
//
// Returns {
//   homefront, footprint,
//   hits: [{ s, hpBefore, hpAfter, destroyed, structureId, broodId, zone }],  // one per impact cell, centre-out
//   destroyedCells: number[],
//   destroyedStructures: string[],      // every structure this crater finished off
//   destroyedBroods: string[],          // every Brood this crater finished off
//   structureKills: string[],           // the kills that SCORE (= destroyedStructures)
//   hpDestroyed, structureHpDestroyed,   // cells destroyed (total / structure cells) — 1 HP each
//   distance,                            // hex distance to nearest surviving structure, or null
//   revealed: number[],                  // cells newly revealed to the attacker (= destroyedCells)
//   rocketsLost: number[],               // rocket cells this crater destroyed (each rocket is gone)
// }
export function applyCraterDamage(homefront, aimS, margin, { seed = null } = {}) {  // eslint-disable-line no-unused-vars
    const hf = cloneHomefront(homefront)
    const subs = hf.subplots

    const footprint = craterFootprint(aimS, margin)
    // A strike with no footprint (washed by interception, or zero margin)
    // never impacts.
    const landed = footprint.length > 0

    const hits = []
    const touchedStructures = new Set()
    const touchedBroods = new Set()
    const rocketsLost = []

    for (const s of footprint) {
        const cell = subs[s]
        if (!cell || cell.destroyed || cell.hp <= 0) continue
        const entry = { s, hpBefore: cell.hp, hpAfter: 0, destroyed: true, structureId: cell.structureId, broodId: cell.broodId ?? null, zone: 'impact' }
        cell.hp = 0
        cell.destroyed = true
        cell.revealed = true
        // A legacy road cell (roads are retired) burns off with the cell.
        if (cell.road) delete cell.road
        if (cell.broodId != null) touchedBroods.add(cell.broodId)
        if (cell.structureId != null) {
            touchedStructures.add(cell.structureId)
            // A rocket sits on one footprint cell and goes with it: destroyed
            // with its cell, it never fires and is no longer drawn on either
            // side, even while its structure still stands.
            const st = hf.structures[cell.structureId]
            if ((st?.rocketCells ?? []).includes(s)) {
                st.rocketCells = st.rocketCells.filter(c => c !== s)
                st.rockets = Math.max(0, (st.rockets || 0) - 1)
                rocketsLost.push(s)
            }
        }
        hits.push(entry)
    }

    // A Brood still UNDER CONSTRUCTION is scaffolding, not armor: ONE destroyed
    // cell kills it (isBroodDead's construction rule — the filter below picks
    // it up: destroyedBroods, corridor reconcile sees no survivor, the corpse
    // sweeps away). The crater keeps its own bounds: the scaffold's surviving cells
    // are NOT destroyed with it — the ground beneath a collapsed construction
    // reverts to open land at the sweep. Mature Bases keep per-cell damage:
    // strength = live cells, each X is -1.

    // ── Resolve aggregates ─────────────────────────────────────────────────────
    const destroyedCells = hits.filter(h => h.destroyed).map(h => h.s)
    let hpDestroyed = 0
    let structureHpDestroyed = 0
    for (const h of hits) {
        const removed = h.hpBefore - h.hpAfter
        hpDestroyed += removed
        if (h.structureId != null) structureHpDestroyed += removed
    }

    // The full-destruction check: a structure dies only when EVERY footprint
    // cell is a crater, and only a structure that was standing before this
    // crater counts as killed by it (re-cratering a corpse registers nothing).
    const destroyedStructures = [...touchedStructures].filter(
        id => isStructureDestroyed(hf, id) && !isStructureDestroyed(homefront, id)
    )
    // Structure kills are the only thing that scores: Broods, partial damage
    // and plain land count for nothing.
    const structureKills = destroyedStructures
    const destroyedBroods = [...touchedBroods].filter(id => isBroodDead(hf, id) && !isBroodDead(homefront, id))

    // Distance intel is computed AFTER destruction resolves (reports next nearest
    // survivor if this crater killed the nearest structure). The stamp is
    // tagged — letter (which structure) + distance — written once and never
    // updated; the impact flag marks the aim cell alongside it. Only a strike
    // that actually LANDED earns either mark: a washed shot was cancelled
    // before impact, so interception denies the bearing along with the damage.
    let distance = null
    if (landed) {
        distance = stampDistanceIntel(hf, aimS).distance
        subs[aimS].impact = true
    }
    // Any kill (this crater's included) may have finished a lettered structure —
    // its stamps everywhere on the board grey out but stay. The fresh stamp
    // above names a survivor by construction, so it is born live.
    sweepDeadStamps(hf)

    return {
        homefront: hf,
        footprint,
        hits,
        destroyedCells,
        destroyedStructures,
        destroyedBroods,
        structureKills,
        hpDestroyed,
        structureHpDestroyed,
        distance,
        revealed: destroyedCells,
        rocketsLost,
    }
}
